import { useMutation, useQueryClient } from "@tanstack/react-query";
import { merchantKeys } from "Api/Queries/Merchants/useGetMerchantQuery";
import { merchantsListKeys } from "Api/Queries/Merchants/useGetMerchantsQuery";
import {
  type ApiProblemDetails,
  type FetchResponse,
  type SendMerchantAccessRequest,
  postMerchantsPublicIDSendAccess,
} from "Infrastructure/Api/Api";
import {
  hasApiProblemError,
  processResponseWithApiProblemDetails,
} from "Infrastructure/Api/ApiUtils";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Hunter.Merchants.CompleteRegistration.SendAccess;

type Parameters = { data: SendMerchantAccessRequest; merchantPublicID: string };

export type SendMerchantAccessError = FetchResponse<
  ApiProblemDetails,
  403 | 409
>;

type Props = {
  onSuccess?: () => void;
  onError?: (error: SendMerchantAccessError) => void;
  isMerchantQueryInvalidationDisabled?: boolean;
};

export const useSendMerchantAccessMutation = ({
  onSuccess,
  onError,
  isMerchantQueryInvalidationDisabled = false,
}: Props = {}) => {
  const queryClient = useQueryClient();
  const { t } = useResource();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      ({ data, merchantPublicID }: Parameters) =>
        postMerchantsPublicIDSendAccess(data, merchantPublicID),
    ),
    onSuccess: (_: unknown, { merchantPublicID }: Parameters) => {
      queryClient.invalidateQueries({
        queryKey: merchantsListKeys.all,
      });
      if (!isMerchantQueryInvalidationDisabled) {
        queryClient.invalidateQueries({
          queryKey: merchantKeys.byMerchantPublicId(merchantPublicID),
        });
      }
      toast.success(t(RESOURCES.SendAccessSuccess));
      onSuccess?.();
    },
    onError: (error: SendMerchantAccessError) => {
      onError?.(error);
      if (error.data?.errors?.CompanyNumber) {
        return;
      }
      // Must precede the generic 403 branch — an internal account is not an existing merchant,
      // so the "already exists" toast would contradict what the form shows on the field.
      if (hasApiProblemError(error, "BackOfficeTarget")) {
        return toast.error(t(RESOURCES.BackOfficeTarget));
      }
      if (error.status === 409 || error.status === 403) {
        return toast.error(t(RESOURCES.MerchantAlreadyExists));
      }
      toast.error(t(RESOURCES.SendAccessError));
    },
  });
};
